// Pure logic for the onboarding wizard (unit-tested in onboarding.test.ts).

export type RoundingMode = 'ceil_x9_99' | 'ending_99' | 'to_1' | 'to_5';

export interface PricingChoice {
  markupPercent: number;
  vatPercent: number;
  deliveryFee: number;
  rounding: RoundingMode;
}

export interface ImportChoice {
  newProductsAsDraft: boolean;
  updateTitles: boolean;
  updateImages: boolean;
  updatePrices: boolean;
}

// Recommended defaults (decision 4 on the review page): never below cost, so rounding goes UP.
export const DEFAULT_PRICING: PricingChoice = {
  markupPercent: 30,
  vatPercent: 21,
  deliveryFee: 0,
  rounding: 'ceil_x9_99',
};

export const DEFAULT_IMPORT: ImportChoice = {
  newProductsAsDraft: true,
  updateTitles: true,
  updateImages: true,
  updatePrices: true,
};

// "Nearest x9.99" (ending_x9_99) is deliberately not offered: it can round below the computed
// price, and for a low markup below cost. It stays available on the Processors page.
export const ROUNDING_OPTIONS: { value: RoundingMode; label: string; hint: string }[] = [
  { value: 'ceil_x9_99', label: 'Up to x9.99', hint: '157.30 → 159.99' },
  { value: 'ending_99', label: 'Nearest .99', hint: '157.30 → 156.99' },
  { value: 'to_1', label: 'Whole euros', hint: '157.30 → 157' },
  { value: 'to_5', label: 'Nearest 5', hint: '157.30 → 155' },
];

// Mirrors roundBase() in the sync workers (standardProcessors.ts / enhancedConfig.ts).
export function roundPrice(n: number, mode: RoundingMode): number {
  switch (mode) {
    case 'ceil_x9_99': {
      const steps = Math.max(0, Math.ceil((n - 9.99) / 10 - 1e-9));
      return Math.round((steps * 10 + 9.99) * 100) / 100;
    }
    case 'ending_99':
      return Math.round(n) - 0.01;
    case 'to_1':
      return Math.round(n);
    case 'to_5':
      return Math.round(n / 5) * 5;
  }
}

// Same order of operations as the workers: tier multiplier, then VAT, then delivery fee, then rounding.
export function computePrice(cost: number, p: PricingChoice): { beforeRounding: number; price: number } {
  const beforeRounding = cost * (1 + p.markupPercent / 100) * (1 + p.vatPercent / 100) + p.deliveryFee;
  return { beforeRounding, price: roundPrice(beforeRounding, p.rounding) };
}

export function clampPricing(p: PricingChoice): PricingChoice {
  const num = (v: number, lo: number, hi: number) => (Number.isFinite(v) ? Math.min(Math.max(v, lo), hi) : lo);
  return {
    markupPercent: num(p.markupPercent, 0, 300),
    vatPercent: num(p.vatPercent, 0, 30),
    deliveryFee: num(p.deliveryFee, 0, 100),
    rounding: p.rounding,
  };
}

// One flat multiplier as the merchant's 'standard' strategy, in the strategies format the
// workers read (pricing.strategies + default_strategy). Tiered pricing stays a Processors-page job.
export function buildPricingConfig(p: PricingChoice) {
  const c = clampPricing(p);
  return {
    strategies: {
      standard: {
        vat_rate: c.vatPercent,
        price_tiers: [
          { min: 0, max: 999999, method: 'multiply', multiplier: Math.round((1 + c.markupPercent / 100) * 10000) / 10000 },
        ],
        delivery_fee: c.deliveryFee,
        rounding_rules: { round_to: c.rounding },
        global_discount_percent: 0,
      },
    },
    default_strategy: 'standard',
  };
}

// Only the fields the wizard asks about. Stock is always kept updated.
export function applyImportChoice(updateSchema: any, i: ImportChoice) {
  const s = updateSchema ?? {};
  return {
    ...s,
    product: { ...(s.product ?? {}), title: i.updateTitles, brand: i.updateTitles, image: i.updateImages },
    variant: { ...(s.variant ?? {}), price: i.updatePrices, stock: true },
    clientConfig: { ...(s.clientConfig ?? {}), newProductStatus: i.newProductsAsDraft ? 'DRAFT' : 'ACTIVE' },
  };
}

// Builds the config written on "Go live". It merges into what deploy_merchant wrote (the store
// location, the template); it never replaces it. A store that already has a pricing setup keeps
// it unless the merchant changed pricing in the wizard.
export function buildGoLiveConfig(
  existing: Record<string, any>,
  opts: { pricing: PricingChoice; importChoice: ImportChoice; setPricing: boolean; clientName?: string },
) {
  const businessRules = existing.business_rules ?? {};
  return {
    ...existing,
    ...(opts.clientName ? { client_name: opts.clientName } : {}),
    ...(opts.setPricing || !existing.pricing ? { pricing: buildPricingConfig(opts.pricing) } : {}),
    business_rules: {
      ...businessRules,
      update_schema: applyImportChoice(businessRules.update_schema, opts.importChoice),
    },
    sync_enabled: true,
  };
}

export function cleanShopifyDomain(raw: string): string | null {
  const host = raw.trim().toLowerCase().replace(/^https?:\/\//, '').split(/[/?#]/)[0];
  const handle = host.replace(/\.myshopify\.com$/, '');
  return /^[a-z0-9][a-z0-9-]*$/.test(handle) ? `${handle}.myshopify.com` : null;
}

export function cleanWooUrl(raw: string): string | null {
  const t = raw.trim().replace(/\/+$/, '');
  try {
    const u = new URL(/^https?:\/\//i.test(t) ? t : `https://${t}`);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname.includes('.')) return null;
    return `${u.protocol}//${u.host}${u.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}
